<template>
  <div class="copilotKitSidebar ac-root" :class="[panelModeClass, { 'is-open': isOpen }]">
    <!-- Floating button: bell for the Action Center, chat bubble for Super Admins (Alerts + Agent).
         The badge counts open alerts only. -->
    <button
      type="button"
      class="copilotKitButton"
      :class="{ open: isOpen }"
      :aria-label="isOpen ? `Close ${panelTitle}` : `Open ${panelTitle}`"
      :aria-expanded="isOpen ? 'true' : 'false'"
      data-test="ac-fab"
      @click="toggle"
    >
      <span v-if="!isOpen && alertCount > 0" class="agent-fab-badge" data-test="ac-fab-badge">{{ badgeText }}</span>
      <span class="copilotKitButtonIcon copilotKitButtonIconOpen" v-html="fabIcon"></span>
      <span class="copilotKitButtonIcon copilotKitButtonIconClose" v-html="CloseIcon"></span>
    </button>

    <!-- Tablet widths only (agent-panel.css): dims the page behind the drawer; tap to close. -->
    <div v-if="isOpen" class="ac-scrim" aria-hidden="true" @click="close"></div>

    <div class="copilotKitWindow" :class="{ open: isOpen }" role="dialog" :aria-label="panelTitle" data-test="ac-window">
      <!-- Phones: the panel is a sheet — swipe down on the handle or header to close. -->
      <div
        class="ac-sheet-handle"
        aria-hidden="true"
        @touchstart="onSheetTouchStart"
        @touchmove="onSheetTouchMove"
        @touchend="onSheetTouchEnd"
      ></div>

      <header class="ac-header" @touchstart="onSheetTouchStart" @touchmove="onSheetTouchMove" @touchend="onSheetTouchEnd">
        <div v-if="isSuperAdmin" class="ac-segments" role="tablist" aria-label="Panel">
          <button
            type="button"
            role="tab"
            class="ac-segment"
            :class="{ 'is-active': mode === 'alerts' }"
            :aria-selected="mode === 'alerts' ? 'true' : 'false'"
            data-test="ac-mode-alerts"
            @click="mode = 'alerts'"
          >
            <AcIcon name="bell" :size="15" />Alerts
            <span v-if="alertCount > 0" class="ac-segment-count">{{ badgeText }}</span>
          </button>
          <button
            type="button"
            role="tab"
            class="ac-segment"
            :class="{ 'is-active': mode === 'agent' }"
            :aria-selected="mode === 'agent' ? 'true' : 'false'"
            data-test="ac-mode-agent"
            @click="mode = 'agent'"
          >
            <AcIcon name="sparkles" :size="15" />Agent
          </button>
        </div>
        <div v-else class="ac-title" data-test="ac-title">
          <AcIcon name="bell" :size="16" />Action Center
        </div>
        <button type="button" class="ac-close" aria-label="Close" data-test="ac-close" @click="close">
          <AcIcon name="x" :size="18" />
        </button>
      </header>

      <ActionCenterPanel
        v-show="!isSuperAdmin || mode === 'alerts'"
        :categories="chipCategories"
        :category="category"
        :focus="focus"
        :focus-filter="focusFilter"
        :sort="sort"
        :groups="alertGroups"
        :empty-state="emptyState"
        :clear-label="clearLabel"
        :undo-text="undo ? undo.text : ''"
        :reply-texts="replyTexts"
        :reply-sending="replySending"
        :busy="busy"
        @select-category="setCategory"
        @toggle-focus="focus = !focus"
        @toggle-sort="sort = sort === 'old' ? 'new' : 'old'"
        @clear-all="clearVisible"
        @undo="undoClear"
        @card-primary="onCardPrimary"
        @card-dismiss="dismissItems([$event])"
        @reply-input="onReplyInput($event.item, $event.text)"
        @reply-send="sendReply"
        @reply-focus="pin($event.id)"
        @reply-blur="onReplyBlur"
        @open-step="navigateTo"
      />

      <!-- Teamio Agent: Super Admins only. Chat behaviour is unchanged by the redesign (visual pass only). -->
      <AgentChat
        v-if="isSuperAdmin"
        v-show="mode === 'agent'"
        :messages="messages"
        :is-running="isRunning"
        :otp-required="otpRequired"
        :user-first-name="userFirstName"
        @send="handleSend"
        @stop="handleStop"
        @submit-otp="handleOtp"
        @open-ai-call="showAICallModal = true"
      />

      <AICallModal
        v-if="isSuperAdmin"
        v-model="showAICallModal"
        :site-id="effectiveSiteId"
        @call-completed="onAICallCompleted"
      />
    </div>
  </div>
</template>

<script>
import AgentChat from './AgentChat.vue';
import AICallModal from './AICallModal.vue';
import AcIcon from './ActionCenter/AcIcon.vue';
import ActionCenterPanel from './ActionCenter/ActionCenterPanel.vue';
import { OpenIcon, CloseIcon, BellIcon } from './agentIcons';
import './agent-panel.css';
import { mapState } from 'vuex';
import {
  db, collection, query, where, updateDoc, doc, orderBy, limit, writeBatch, deleteField,
} from '../../store/firebase.config';
import { safeSnapshot } from '@/store/firestoreListen';
import {
  CONVERSATION_WINDOW, intakeConversation, shouldResetDismissMap, computeMissedSmsAlerts, resolveConfirmKeywords,
} from '../../utils/actionCenterSms';
import { dismissScopeFromSettings } from '../../utils/actionCenterReadState';
import {
  CATEGORIES, FOCUS_FILTERS, buildAlertItems, countByCategory, filterItems, sortItems, groupItemsByDay, emptyStateFor,
} from '../../utils/actionCenterAlerts';
import { resolveTimeZone } from '../../utils/actionCenterTime';
import { canViewPatientPaymentsPage } from '../../utils/actionCenterPaymentLink';
import { classifyNonDelivery } from '../../utils/messaging';
import { optOutResultFromSendFailure } from '../../utils/textConsent';
import axios from 'axios';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

// Alert rules live in src/utils/actionCenter*.js (node-tested against the real files):
//   actionCenterSms       — missed-SMS detection (age cap, conversation window, phantom guards, confirm words)
//   actionCenterReadState — ACTION_CENTER_DISMISS_SCOPE: whose clear hides a card
//   actionCenterAlerts    — what each card says, chips, focus filters, sort, Today/Earlier
// This component owns the Firestore listeners, the writes, sends and navigation.

// Firestore allows at most 500 writes in one batch.
const BATCH_LIMIT = 500;
// How long "Cleared N alerts · Undo" stays up.
const UNDO_MS = 5000;
// Refresh ages, Today/Earlier and the SMS 15/30-minute tiers while the panel sits open.
const TICK_MS = 60000;

export default {
  name: 'AgentSidebar',
  components: { AgentChat, AICallModal, AcIcon, ActionCenterPanel },
  props: {
    title: { type: String, default: 'Teamio Agent' },
    agentUrl: { type: String, default: '' },
    apiKey: { type: String, default: '' },
    siteId: { type: String, default: '' },
  },
  data() {
    return {
      isOpen: false,
      isRunning: false,
      otpRequired: false,
      messages: [],
      showAICallModal: false,
      currentRun: null,
      threadId: null,
      OpenIcon,
      CloseIcon,
      BellIcon,
      // Super Admins switch between Alerts and Agent; everyone else only has Alerts.
      mode: 'alerts',
      botEvents: [],
      eventsUnsubscribe: null,
      missedSmsAlerts: [],
      missedSmsCheckInterval: null,
      convoUnsubscribe: null,
      siteConversations: [],
      // List controls.
      category: 'all',
      focus: false,
      sort: 'new',
      now: Date.now(),
      // Keyed by card id (conversation SID or bot-events doc id).
      replyTexts: {},
      replySending: {},
      // Cards with an open quick-reply draft stay on screen even if a teammate clears them.
      pinned: {},
      // Cards with a clear write in flight (double-click guard).
      busy: {},
      // Plan questions answered from the card: hidden now, before the resolve event round-trips.
      resolvedLocally: {},
      // { text, entries: [{ source, id, prev }] } while the undo bar shows.
      undo: null,
      undoTimer: null,
      sheetTouchStartY: null,
      sheetTouchDeltaY: 0,
    };
  },

  computed: {
    ...mapState('customer', ['settings']),

    effectiveSiteId() {
      // siteId prop comes from App.vue (customer.siteID). For Super Admin without a site
      // selected it may be empty — fall back to the store directly.
      return this.siteId || this.$store.state.customer.siteID || '';
    },

    allSettings() {
      if (!this.settings) return {};
      return this.settings.reduce((map, s) => { map[s.Property] = s; return map; }, {});
    },

    // Replies that mean "handled, nothing for the front desk to do" — the
    // appointment-reminder confirmation words (site-configurable via Apt Reminder settings).
    confirmKeywords() {
      return resolveConfirmKeywords(this.allSettings);
    },

    // ACTION_CENTER_DISMISS_SCOPE (Settings → General): 'office' (default) clears an alert for
    // everyone, 'individual' only for the person who cleared it.
    dismissScope() {
      return dismissScopeFromSettings(this.settings);
    },

    // Card times render in the practice's timezone; no/unknown setting → the browser's zone.
    timeZone() {
      const row = this.allSettings.GENERAL_TIMEZONE;
      return resolveTimeZone(row && row.PropertyValue);
    },

    // Super Admins get Alerts + the Teamio Agent (chat + AI Call modal). Everyone else gets the
    // Action Center only: bell FAB, "Action Center" header, no chat.
    //
    // Preview override: appending `?actionCenterPreview=1` to any URL forces
    // this computed to return false, so a Super Admin can QA the non-admin
    // experience without changing their Vuex role (which would also trip the
    // CASL ability rebuild + session-refresh logout). Drop the param to
    // revert. Purely client-side and scoped to this one computed.
    isSuperAdmin() {
      if (this.$route && this.$route.query && this.$route.query.actionCenterPreview === '1') {
        return false;
      }
      const user = this.$store.state.customer.user;
      return !!(user && user.Role === 'Super Admin');
    },

    myEmployeeID() {
      const user = this.$store.state.customer.user;
      return user && user.EmployeeID;
    },

    userFirstName() {
      const user = this.$store.state.customer.user || {};
      return user.FName || String(user.Name || '').trim().split(/\s+/)[0] || '';
    },

    panelTitle() {
      return this.isSuperAdmin ? this.title : 'Action Center';
    },

    fabIcon() {
      return this.isSuperAdmin ? OpenIcon : BellIcon;
    },

    panelModeClass() {
      return this.isSuperAdmin ? 'mode-full' : 'mode-actions-only';
    },

    // Every card the viewer should see — the office clearing setting and open drafts applied — unsorted.
    alertItems() {
      return buildAlertItems({
        smsAlerts: this.missedSmsAlerts,
        botEvents: this.botEvents.filter((e) => !this.resolvedLocally[e.id]),
        employeeID: this.myEmployeeID,
        scope: this.dismissScope,
        pinned: this.pinned,
        timeZone: this.timeZone,
        now: this.now,
        canViewPayments: this.canViewPatientPayments,
      });
    },

    // "Payment received" opens My Account → Patient Payments for those who can see that list.
    canViewPatientPayments() {
      const user = this.$store.state.customer.user;
      const setting = this.allSettings.TEXT_TO_PAY_OD_WRITE_ENABLED;
      return canViewPatientPaymentsPage({
        role: user && user.Role,
        odWriteEnabled: setting && setting.PropertyValue,
      });
    },

    alertCounts() {
      return countByCategory(this.alertItems);
    },

    // The floating-button badge counts open alerts only.
    alertCount() {
      return this.alertCounts.all;
    },

    badgeText() {
      return this.alertCount > 99 ? '99+' : String(this.alertCount);
    },

    chipCategories() {
      return CATEGORIES.map((c) => ({ ...c, count: this.alertCounts[c.key] || 0 }));
    },

    focusFilter() {
      return FOCUS_FILTERS[this.category] || FOCUS_FILTERS.all;
    },

    visibleItems() {
      const filtered = filterItems(this.alertItems, {
        category: this.category, focus: this.focus, now: this.now, timeZone: this.timeZone,
      });
      return sortItems(filtered, this.sort);
    },

    alertGroups() {
      return groupItemsByDay(this.visibleItems, { timeZone: this.timeZone, now: this.now });
    },

    emptyState() {
      return emptyStateFor(this.category, { focus: this.focus });
    },

    // Clear all acts on exactly what's listed: the chip plus the focus filter.
    clearLabel() {
      if (this.focus) return 'Clear these';
      if (this.category === 'all') return 'Clear all';
      const cat = CATEGORIES.find((c) => c.key === this.category);
      return `Clear ${cat ? cat.label : 'all'}`;
    },
  },

  watch: {
    siteId: {
      immediate: true,
      handler(val) {
        // ALWAYS tear down before (re)subscribing. Both subscribe methods are
        // idempotency-guarded (`if (this.eventsUnsubscribe) return;`), so on a site
        // SWITCH — where siteId goes truthy -> truthy — they would early-return and
        // leave both listeners bound to the PREVIOUS site. The panel would then show
        // the previous practice's patients: names, phone numbers, message previews.
        //
        // Not always visible, which is why it survived: customer.js `selectSite` does
        // `router.go(0)` (full reload, masks this) when the post-switch route equals the
        // current one, and `router.push` (SPA nav, no reload) otherwise. AgentSidebar
        // lives in App.vue as a SIBLING of <router-view :key=...>, so the key re-creates
        // page components but not this one — it survives the navigation with stale
        // listeners. Tearing down unconditionally is correct either way.
        this.unsubscribeFromBotEvents();
        if (val) this.subscribeToBotEvents();
      },
    },
    // An admin switching the office clearing setting re-applies it without waiting for the next tick.
    dismissScope() {
      this.computeMissedSmsAlerts();
    },
    isOpen(open) {
      if (open) this.tick();
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.threadId = this.generateUUID();
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    this.unsubscribeFromBotEvents();
  },

  methods: {
    toggle() { this.isOpen = !this.isOpen; },
    close() { this.isOpen = false; },
    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) this.close();
    },

    onAICallCompleted({ mode, patient, result }) {
      // Drop a synthetic assistant message into the chat so the outcome
      // lives in the same timeline as the rest of the agent's work.
      const id = `ai-call-${Date.now()}`;
      let content;
      if (mode === 'one') {
        const name = patient ? this.formatName(patient) : 'patient';
        if (result && result.success) {
          const verdict = result.outcome || (result.mode === 'real' ? 'call initiated' : 'simulation completed');
          content = `📞 AI call to ${name}: ${verdict} (${result.mode || 'unknown mode'}).`;
        } else {
          content = `📞 AI call to ${name} did not complete: ${result && (result.message || result.reason) || 'unknown error'}.`;
        }
      } else {
        const t = (result && result.totals) || {};
        const status = result && result.aborted ? 'aborted'
                     : result && result.completed ? 'completed'
                     : 'failed';
        content = `📋 Batch AI call ${status}: ${t.attempted || 0} attempted, ${t.succeeded || 0} initiated, ${t.skipped || 0} skipped.`;
      }
      this.messages.push({ id, type: 'assistant', content });
    },

    formatName(p) {
      if (!p) return 'patient';
      if (p.Patient) {
        if (p.Patient.includes(',')) {
          const [ln, fn] = p.Patient.split(',').map(s => s.trim());
          return `${fn} ${ln}`;
        }
        return p.Patient;
      }
      return [p.FName, p.LName].filter(Boolean).join(' ') || `PatNum ${p.PatNum}`;
    },

    handleSend(text) {
      this.messages.push({ id: `user-${Date.now()}`, type: 'user', content: text });
      this.otpRequired = false;

      // Handle locally — no API needed
      if (/what (can you|do you|are you able to)|help me with|capabilities|what do you do/i.test(text)) {
        this.showCapabilitiesMessage();
        return;
      }
      if (/show.*(unscheduled|overdue|recall list|patient list)|list.*patient|all patient/i.test(text)) {
        this.showListLimitationMessage();
        return;
      }

      // Route to agent server for automations, bot stream for everything else
      // Check if last assistant message was an automation request (carry context forward)
      const lastAssistantMsg = [...this.messages].reverse().find(m => m.type === 'assistant' && m.content);
      const lastWasAutomation = lastAssistantMsg && /patient('s)? (name|first|last|full)|which patient|provide.*name|need.*name|patient.*details|date of birth|member id/i.test(lastAssistantMsg.content);
      const isAutomation = lastWasAutomation || /verify insurance|check insurance|check eligibility|recall|follow.?up call|call.*patient|browse.*portal|navigate.*portal|insurance.*portal|do it for|try.*for|run.*for/i.test(text);

      if (!this.agentUrl) {
        this.runMockAgent();
      } else if (isAutomation) {
        this.startAgentRun({ input: text, siteId: this.effectiveSiteId });
      } else {
        this.startBotStream(text);
      }
    },

    showCapabilitiesMessage() {
      const id = `cap-${Date.now()}`;
      this.messages.push({ id, type: 'assistant', content: '', isStreaming: true });
      const reply = `Here's what I can help you with:\n\n**🛡️ Insurance Verification**\nBrowse insurance portals to verify patient eligibility and extract benefit details. Just tell me the patient name and carrier.\n\n**📞 Patient Recall Calls**\nAutomatically call patients who are overdue for a cleaning or have unscheduled treatment. Tell me the patient name and I'll handle the rest.\n\n**💬 Practice Questions**\nAsk me anything about how Teamio works — appointments, forms, billing, conversations, settings, and more.\n\n**🔔 Action Center**\nI'll surface missed SMS alerts and important events so nothing falls through the cracks.\n\nMore automations are coming soon. Just type what you need!`;

      let i = 0;
      const tick = () => {
        const chunk = Math.floor(Math.random() * 4) + 2;
        const msg = this.messages.find(m => m.id === id);
        if (!msg) return;
        this.$set(msg, 'content', reply.slice(0, i + chunk));
        i += chunk;
        if (i < reply.length) {
          setTimeout(tick, 14);
        } else {
          this.$set(msg, 'content', reply);
          this.$set(msg, 'isStreaming', false);
          this.messages.push({
            id: `sug-${Date.now()}`,
            type: 'suggestions',
            suggestions: [
              { icon: '🛡️', text: 'Verify insurance for a patient' },
              { icon: '📞', text: 'Do a recall follow-up call' },
              { icon: '📅', text: 'How do I set up appointment reminders?' },
              { icon: '📋', text: 'Show me unscheduled treatment patients' },
            ],
          });
        }
      };
      setTimeout(tick, 300);
    },
    handleStop() {
      this.currentRun = null;
      this.isRunning = false;
    },
    handleOtp(code) {
      this.otpRequired = false;
      this.messages.push({ id: `otp-${Date.now()}`, type: 'user', content: `[OTP: ${code}]` });
      if (!this.agentUrl) {
        this.runMockOtp();
      } else {
        this.startAgentRun({ otp: code, siteId: this.effectiveSiteId });
      }
    },

    // ── SSE agent client ──────────────────────────────────────────────────────
    async startAgentRun(params) {
      this.isRunning = true;
      const runRef = {};
      this.currentRun = runRef;

      try {
        const response = await fetch(this.agentUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let chunk = await reader.read();

        while (!chunk.done) {
          buffer += decoder.decode(chunk.value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();
          for (const part of parts) {
            const dataLine = part.split('\n').find(l => l.startsWith('data:'));
            if (!dataLine) continue;
            try {
              const event = JSON.parse(dataLine.slice(5).trim());
              if (this.currentRun !== runRef) return;
              this.handleAgentEvent(event);
            } catch (_) { /* skip malformed lines */ }
          }
          chunk = await reader.read(); // eslint-disable-line no-await-in-loop
        }
      } catch (err) {
        if (this.currentRun === runRef) {
          this.isRunning = false;
          const friendly = err && err.message === 'Failed to fetch'
            ? "I couldn't reach the agent server. It may be offline or still starting up — please try again in a moment."
            : `Error: ${err && err.message}`;
          this.messages.push({ id: `err-${Date.now()}`, type: 'assistant', content: friendly });
          this.maybeAppendSuggestions('automation');
        }
      }
    },

    handleAgentEvent(event) {
      switch (event.type) {
        case 'STEP_STARTED':
          this.messages.push({ id: `step-${event.stepName}`, type: 'step', content: event.stepName, status: 'running' });
          break;
        case 'STEP_FINISHED': {
          const step = this.messages.find(m => m.id === `step-${event.stepName}`);
          if (step) this.$set(step, 'status', 'done');
          break;
        }
        case 'TEXT_MESSAGE_START':
          this.messages.push({ id: event.messageId, type: 'assistant', content: '', isStreaming: true });
          break;
        case 'TEXT_MESSAGE_CONTENT': {
          const msg = this.messages.find(m => m.id === event.messageId);
          if (msg) this.$set(msg, 'content', msg.content + (event.delta || ''));
          break;
        }
        case 'TEXT_MESSAGE_END': {
          const msg = this.messages.find(m => m.id === event.messageId);
          if (msg) this.$set(msg, 'isStreaming', false);
          break;
        }
        case 'TOOL_CALL_START':
          this.messages.push({ id: event.toolCallId, type: 'tool', toolName: event.toolCallName, args: '', status: 'running' });
          break;
        case 'TOOL_CALL_END': {
          const msg = this.messages.find(m => m.id === event.toolCallId);
          if (msg) {
            this.$set(msg, 'args', JSON.stringify(event.toolCallArgs || {}));
            this.$set(msg, 'status', 'done');
          }
          break;
        }
        case 'STATE_SNAPSHOT':
          if (event.snapshot && event.snapshot.extractedData) {
            this.messages.push({ id: `data-${Date.now()}`, type: 'data', title: event.title || 'Results', data: event.snapshot.extractedData });
          }
          break;
        case 'CUSTOM':
          if (event.name === 'otp_required') this.otpRequired = true;
          break;
        case 'RUN_FINISHED':
          this.isRunning = false;
          this.maybeAppendSuggestions('automation');
          break;
        case 'RUN_ERROR':
          this.isRunning = false;
          this.messages.push({ id: `err-${Date.now()}`, type: 'assistant', content: `Error: ${event.message}` });
          this.maybeAppendSuggestions('automation');
          break;
      }
    },

    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
    },

    // ── Knowledge base Q&A — streams from bot API ─────────────────────────────
    async startBotStream(text) {
      const streamUrl = process.env.VUE_APP_BOT_STREAM_URL;
      const botApiUrl = process.env.VUE_APP_TEAMIO_API_URL;
      // Teamio Bot API key — from .env.<mode>.local (VUE_APP_TEAMIO_BOT_API_KEY), never a literal here.
      // Pinned by cypress/node-tests/bot-api-key-not-hardcoded.test.js.
      const apiKey = process.env.VUE_APP_TEAMIO_BOT_API_KEY || '';

      // Build conversation context from current messages (last 10, text only)
      const conversationContext = this.messages
        .filter(m => (m.type === 'user' || m.type === 'assistant') && m.content)
        .slice(-10)
        .map(m => ({ content: m.content, isUser: m.type === 'user' }));

      const payload = {
        message: text,
        conversation: conversationContext,
        siteID: null, // platform mode — uses Teamio knowledge base
        threadID: this.threadId,
      };

      const msgId = `bot-${Date.now()}`;
      this.messages.push({ id: msgId, type: 'assistant', content: '', isStreaming: true });
      this.isRunning = true;

      const finalize = (content) => {
        const msg = this.messages.find(m => m.id === msgId);
        if (msg) {
          this.$set(msg, 'content', content);
          this.$set(msg, 'isStreaming', false);
        }
        this.isRunning = false;
        this.maybeAppendSuggestions('bot');
      };

      try {
        if (streamUrl) {
          // Streaming mode — NDJSON token events
          const response = await fetch(streamUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
            body: JSON.stringify(payload),
          });
          if (!response.ok) throw new Error(`Bot API ${response.status}`);

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let fullContent = '';

          let readDone = false;
          while (!readDone) {
            const { value, done } = await reader.read(); // eslint-disable-line no-await-in-loop
            if (done) { readDone = true; continue; }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();
            for (const line of lines) {
              if (!line.trim()) continue;
              try {
                const event = JSON.parse(line);
                if (event.type === 'token') {
                  fullContent += event.content;
                  const msg = this.messages.find(m => m.id === msgId);
                  if (msg) this.$set(msg, 'content', fullContent);
                } else if (event.type === 'message') {
                  fullContent = event.content;
                  const msg = this.messages.find(m => m.id === msgId);
                  if (msg) this.$set(msg, 'content', fullContent);
                } else if (event.type === 'error') {
                  // Stream Lambda error — fall back to buffered API
                  console.warn('[AgentSidebar] Stream error, falling back to buffered API:', event.content);
                  const fallback = await fetch(`${botApiUrl}/get-bot-response`, { // eslint-disable-line no-await-in-loop
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
                    body: JSON.stringify(payload),
                  });
                  if (!fallback.ok) throw new Error(`Bot API ${fallback.status}`);
                  const data = await fallback.json();
                  let answer = data.answer || data.response || 'Sorry, I could not process that.';
                  if (!data.answer && data.message) {
                    try { answer = JSON.parse(data.message).answer || data.message; } catch (e) { answer = data.message; }
                  }
                  finalize(answer);
                  return;
                }
              } catch (parseErr) {
                console.warn('[AgentSidebar] NDJSON parse error:', parseErr.message);
              }
            }
          }
          finalize(fullContent);
        } else {
          // Fallback — buffered mode
          const response = await fetch(`${botApiUrl}/get-bot-response`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
            body: JSON.stringify(payload),
          });
          if (!response.ok) throw new Error(`Bot API ${response.status}`);
          const data = await response.json();
          let answer = data.answer || data.response || 'Sorry, I could not process that.';
          if (!data.answer && data.message) {
            try { answer = JSON.parse(data.message).answer || data.message; } catch (e) { answer = data.message; }
          }
          finalize(answer);
        }
      } catch (err) {
        console.error('[AgentSidebar] Bot stream error:', err);
        finalize(`Sorry, I ran into an issue: ${err.message}`);
      }
    },

    showListLimitationMessage() {
      const id = `list-${Date.now()}`;
      this.messages.push({ id, type: 'assistant', content: '', isStreaming: true });
      const reply = `I can look up and call **individual patients** by name, but I can't pull a full list directly — that lives in your practice management software.\n\nHere's what I can do:\n- **"Call John Smith for unscheduled treatment"** — I'll find them and make the call\n- **"Do a recall follow-up with Sarah Johnson"** — same for overdue cleanings\n\nWho would you like me to follow up with?`;
      let i = 0;
      const tick = () => {
        const chunk = Math.floor(Math.random() * 4) + 2;
        const msg = this.messages.find(m => m.id === id);
        if (!msg) return;
        this.$set(msg, 'content', reply.slice(0, i + chunk));
        i += chunk;
        if (i < reply.length) {
          setTimeout(tick, 14);
        } else {
          this.$set(msg, 'content', reply);
          this.$set(msg, 'isStreaming', false);
          this.messages.push({
            id: `sug-${Date.now()}`,
            type: 'suggestions',
            suggestions: [
              { icon: '📞', text: 'Recall follow-up with a specific patient' },
              { icon: '🦷', text: 'Call a patient for unscheduled treatment' },
              { icon: '🛡️', text: 'Verify insurance for a patient' },
            ],
          });
        }
      };
      setTimeout(tick, 300);
    },

    maybeAppendSuggestions(source) {
      // Show "what next?" pills after every completed run so users always have
      // an obvious next step. `source` is 'automation' (agent-server RUN_FINISHED)
      // or 'bot' (knowledge-base Q&A finalize); anything else falls back to generic.
      const last = this.messages[this.messages.length - 1];
      if (last && last.type === 'suggestions') return; // avoid stacking duplicates

      let suggestions;
      if (source === 'automation') {
        suggestions = [
          { icon: '🛡️', text: 'Verify insurance for another patient' },
          { icon: '📞', text: 'Do a recall follow-up call' },
          { icon: '✨', text: 'What else can you help me with?' },
        ];
      } else if (source === 'bot') {
        suggestions = [
          { icon: '🛡️', text: 'Verify insurance for a patient' },
          { icon: '📞', text: 'Do a recall follow-up call' },
          { icon: '✨', text: 'What can you help me with?' },
        ];
      } else {
        suggestions = [
          { icon: '📞', text: 'Do a recall follow-up call' },
          { icon: '🛡️', text: 'Verify insurance for a patient' },
          { icon: '✨', text: 'What can you help me with?' },
        ];
      }

      this.messages.push({
        id: `sug-${Date.now()}`,
        type: 'suggestions',
        suggestions,
      });
    },

    // ── Action Center ─────────────────────────────────────────────────────────

    subscribeToBotEvents() {
      if (this.eventsUnsubscribe) return;

      const siteID = this.$store.state.customer.siteID;
      const user = this.$store.state.customer.user;
      if (!siteID || !user || !user.Role) return;

      const allowedRoles = ['Super Admin', 'Admin', 'Manager', 'Employee'];
      if (!allowedRoles.includes(user.Role)) return;

      const eventsRef = collection(db, 'bot-events');
      const q = query(
        eventsRef,
        where('siteID', '==', siteID),
        where('roles', 'array-contains', user.Role),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      this.eventsUnsubscribe = safeSnapshot(q, (snapshot) => {
        const events = [];
        snapshot.forEach((docSnap) => {
          events.push({ id: docSnap.id, ...docSnap.data() });
        });
        this.botEvents = events;
        // A resolved doc leaves this query — its local "already handled" mark is no longer needed.
        const live = new Set(events.map((e) => e.id));
        Object.keys(this.resolvedLocally).forEach((id) => {
          if (!live.has(id)) this.$delete(this.resolvedLocally, id);
        });
      }, {
        source: 'agentSidebar/botEvents',
        onError: (err) => {
          console.error('[AgentSidebar] Bot events listener error:', err);
        },
      });

      this.subscribeToSiteConversations();
      this.missedSmsCheckInterval = setInterval(() => this.tick(), TICK_MS);
    },

    unsubscribeFromBotEvents() {
      if (this.eventsUnsubscribe) { this.eventsUnsubscribe(); this.eventsUnsubscribe = null; }
      if (this.convoUnsubscribe) { this.convoUnsubscribe(); this.convoUnsubscribe = null; }
      if (this.missedSmsCheckInterval) { clearInterval(this.missedSmsCheckInterval); this.missedSmsCheckInterval = null; }
      this.missedSmsAlerts = [];
      this.siteConversations = [];
      // Also drop the rendered bot events. The listener is gone, so nothing will
      // overwrite these until the NEXT site's first snapshot arrives — leaving the
      // previous practice's patient names on screen in the meantime.
      this.botEvents = [];
      // Drafts, pins and a pending undo belong to the previous practice's cards.
      this.replyTexts = {};
      this.replySending = {};
      this.pinned = {};
      this.busy = {};
      this.resolvedLocally = {};
      this.hideUndo();
    },

    subscribeToSiteConversations() {
      if (this.convoUnsubscribe) return;

      const siteID = this.$store.state.customer.siteID;
      if (!siteID) return;

      const convosRef = collection(db, 'conversations');
      const q = query(
        convosRef,
        where('siteID', '==', siteID),
        orderBy('dateUpdated', 'desc'),
        limit(CONVERSATION_WINDOW)
      );

      this.convoUnsubscribe = safeSnapshot(q, (snapshot) => {
        const conversations = [];
        snapshot.forEach((docSnap) => {
          // Phantom guards, timestamp coercion and the inbound test: actionCenterSms.intakeConversation.
          const convo = intakeConversation(docSnap.id, docSnap.data());
          if (!convo) return;
          conversations.push(convo);
          if (shouldResetDismissMap(convo)) {
            updateDoc(doc(db, 'conversations', docSnap.id), { agentDismissedBy: {} }).catch(() => {});
          }
        });
        this.siteConversations = conversations;
        this.computeMissedSmsAlerts();
      }, {
        source: 'agentSidebar/siteConversations',
        onError: (err) => {
          console.error('[AgentSidebar] Conversations listener error:', err);
        },
      });
    },

    computeMissedSmsAlerts() {
      // Rules (15/30-minute tiers, 7-day cap, confirmation words, whose dismissal counts):
      // actionCenterSms.computeMissedSmsAlerts.
      this.missedSmsAlerts = computeMissedSmsAlerts(this.siteConversations, {
        now: Date.now(),
        employeeID: this.myEmployeeID,
        keywords: this.confirmKeywords,
        scope: this.dismissScope,
        pinned: this.pinned,
      });
    },

    tick() {
      this.now = Date.now();
      this.computeMissedSmsAlerts();
    },

    setCategory(key) {
      this.category = key;
      this.focus = false;
    },

    notify(kind, message) {
      if (this.$toast && typeof this.$toast[kind] === 'function') this.$toast[kind](message);
      else if (kind === 'error' || kind === 'warning') console.error(`[AgentSidebar] ${message}`);
    },

    isViewportBelow(px) {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia(`(max-width: ${px - 0.02}px)`).matches;
    },

    navigateTo(to) {
      if (!to) return;
      const pushed = this.$router.push(to);
      if (pushed && typeof pushed.catch === 'function') pushed.catch(() => {}); // same-route clicks
      // The phone sheet and the tablet drawer cover the page, so step aside. Desktop keeps the panel open.
      if (this.isViewportBelow(1024)) this.close();
    },

    onCardPrimary(item) {
      const primary = item && item.primary;
      if (!primary || !primary.to) return;
      // 'navigate' and 'sendNewLink' both route. Send new link opens the patient's Collect Payment dialog
      // pre-filled (src/utils/actionCenterPaymentLink.js): the operator reviews and sends it there, and
      // PAYMENT_LINK_RESENT then clears the card for everyone. Nothing is ever resent automatically.
      this.navigateTo(primary.to);
    },

    // ── Quick reply ───────────────────────────────────────────────────────────

    pin(id) {
      if (!id || this.pinned[id]) return;
      this.$set(this.pinned, id, true);
      this.computeMissedSmsAlerts();
    },

    unpin(id) {
      if (!id || !this.pinned[id]) return;
      this.$delete(this.pinned, id);
      this.computeMissedSmsAlerts();
    },

    onReplyInput(item, text) {
      this.$set(this.replyTexts, item.id, text);
      if (String(text || '').trim()) this.pin(item.id);
    },

    onReplyBlur(item) {
      if (!String(this.replyTexts[item.id] || '').trim() && !this.replySending[item.id]) this.unpin(item.id);
    },

    async sendReply(item) {
      const text = String(this.replyTexts[item.id] || '').trim();
      const siteID = this.$store.state.customer.siteID;
      if (!text || !siteID || this.replySending[item.id]) return;

      this.$set(this.replySending, item.id, true);
      try {
        const sent = item.source === 'sms'
          ? await this.sendSmsReply(item, text, siteID)
          : await this.sendPlanReply(item, text, siteID);
        if (!sent) return; // refusal already explained; the draft stays
        this.$set(this.replyTexts, item.id, '');
        this.unpin(item.id);
        this.notify('success', `Reply sent to ${item.title}`);
      } catch (err) {
        console.error('[AgentSidebar] Quick reply failed:', err);
        this.notify('error', item.source === 'sms'
          ? "Couldn't send the reply — try again, or open the conversation."
          : "Couldn't post the reply — try again, or open the plan.");
      } finally {
        this.$set(this.replySending, item.id, false);
      }
    },

    // Same request as the Conversations composer. Opt-out / undeliverable refusals come back as HTTP 200
    // { success:false } (or a 4xx from an older backend): explain them and keep the draft.
    async sendSmsReply(item, text, siteID) {
      const user = this.$store.state.customer.user || {};
      let data;
      try {
        const res = await axios.post('/send-message', {
          roomId: item.conversationSID,
          message: { content: text, author: user.Name || 'TeamIO', attributes: { outbound: true } },
          backgroundTaskPayload: { siteID, smsText: text, employeeName: user.Name || '' },
        });
        data = res && res.data;
      } catch (err) {
        const errData = err && err.response && err.response.data;
        if (!errData) throw err;
        this.explainNotSent(errData);
        return false;
      }
      if (data && data.success === false) {
        this.explainNotSent(data);
        return false;
      }
      return true;
    },

    explainNotSent(data) {
      const optOut = optOutResultFromSendFailure(data);
      const { message, isNonDelivery } = classifyNonDelivery(data);
      this.notify(optOut || isNonDelivery ? 'warning' : 'error', optOut ? optOut.reason : message);
    },

    // Office reply to a patient's question on a treatment plan — the endpoint the Tx Presenter builder
    // uses. A held AI draft is replaced by this reply, like the builder's "replace" (store/txPresenter.js
    // postTcMessage). The backend then resolves the card for everyone (TX_PLAN_QUESTION_RESOLVED).
    async sendPlanReply(item, text, siteID) {
      const q = item.txQuestion || {};
      const user = this.$store.state.customer.user || {};
      const tcEmployeeNum = user.EmployeeID || user.employeeID || user._id || null;
      if (!q.patNum || !q.treatPlanNum || !tcEmployeeNum) throw new Error('Missing plan or employee for the reply');
      const res = await axios.post('/post-tc-message', {
        siteID,
        patNum: q.patNum,
        treatPlanNum: q.treatPlanNum,
        tcEmployeeNum,
        body: text,
        replacesCommentID: q.heldAiCommentID || null,
      });
      if (!res || !res.data || !res.data.success) {
        throw new Error((res && res.data && res.data.message) || 'Failed to post reply');
      }
      this.markPlanQuestionsResolved(item.id);
      return true;
    },

    // Every open question card on the same plan is answered by this reply (same resolveKey as the backend).
    markPlanQuestionsResolved(eventId) {
      const evt = this.botEvents.find((e) => e.id === eventId);
      const key = evt && evt.resolveKey;
      this.$set(this.resolvedLocally, eventId, true);
      if (!key) return;
      this.botEvents.forEach((e) => {
        if (e.resolveKey === key) this.$set(this.resolvedLocally, e.id, true);
      });
    },

    // ── Clearing + undo ───────────────────────────────────────────────────────
    // A clear always records WHO and WHEN (bot-events.readBy.{employeeID}, conversations.agentDismissedBy.
    // {employeeID}). Whether that hides the card for the rest of the office is the READ rule
    // (ACTION_CENTER_DISMISS_SCOPE), so one write path serves both settings.

    clearVisible() {
      const items = this.visibleItems.slice();
      if (items.length) this.dismissItems(items);
    },

    async dismissItems(items) {
      const employeeID = this.myEmployeeID;
      if (!employeeID) {
        this.notify('error', "Couldn't clear — please reload and try again.");
        return;
      }
      const targets = (items || []).filter((item) => item && item.id && !this.busy[item.id]);
      if (!targets.length) return;

      const at = Date.now();
      const entries = targets.map((item) => this.clearEntryFor(item, employeeID));
      targets.forEach((item) => {
        this.$set(this.busy, item.id, true);
        this.unpin(item.id);
      });
      this.patchLocalReadState(entries, employeeID, () => at);
      this.showUndo(entries);

      try {
        await this.writeReadState(entries, employeeID, () => at);
      } catch (err) {
        console.error('[AgentSidebar] Clear failed:', err);
        this.patchLocalReadState(entries, employeeID, (entry) => entry.prev);
        this.hideUndo();
        this.notify('error', "Couldn't clear — please try again.");
      } finally {
        targets.forEach((item) => this.$set(this.busy, item.id, false));
      }
    },

    // Remembers this employee's previous entry so Undo restores exactly what was there.
    clearEntryFor(item, employeeID) {
      const key = String(employeeID);
      const own = (map) => (map && typeof map === 'object' && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined);
      if (item.source === 'sms') {
        const convo = this.siteConversations.find((c) => c.sid === item.id);
        return { source: 'sms', id: item.id, prev: own(convo && convo.agentDismissedBy) };
      }
      const evt = this.botEvents.find((e) => e.id === item.id);
      return { source: 'event', id: item.id, prev: own(evt && evt.readBy) };
    },

    async writeReadState(entries, employeeID, valueFor) {
      const key = String(employeeID);
      for (let i = 0; i < entries.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        entries.slice(i, i + BATCH_LIMIT).forEach((entry) => {
          const isSms = entry.source === 'sms';
          batch.update(doc(db, isSms ? 'conversations' : 'bot-events', entry.id), {
            [`${isSms ? 'agentDismissedBy' : 'readBy'}.${key}`]: valueFor(entry),
          });
        });
        await batch.commit(); // eslint-disable-line no-await-in-loop
      }
    },

    // Mirror a clear / undo into the local copies so the card moves immediately; the listeners bring the
    // server copy right after. `valueFor` returning undefined removes this employee's entry.
    patchLocalReadState(entries, employeeID, valueFor) {
      const key = String(employeeID);
      const patch = (map, value) => {
        const next = { ...(map && typeof map === 'object' ? map : {}) };
        if (value === undefined) delete next[key];
        else next[key] = value;
        return next;
      };
      const sms = {};
      const events = {};
      entries.forEach((entry) => { (entry.source === 'sms' ? sms : events)[entry.id] = entry; });
      if (Object.keys(sms).length) {
        this.siteConversations = this.siteConversations.map((c) => (sms[c.sid]
          ? { ...c, agentDismissedBy: patch(c.agentDismissedBy, valueFor(sms[c.sid])) }
          : c));
        this.computeMissedSmsAlerts();
      }
      if (Object.keys(events).length) {
        this.botEvents = this.botEvents.map((e) => (events[e.id]
          ? { ...e, readBy: patch(e.readBy, valueFor(events[e.id])) }
          : e));
      }
    },

    showUndo(entries) {
      this.hideUndo();
      const n = entries.length;
      this.undo = { entries, text: `Cleared ${n} ${n === 1 ? 'alert' : 'alerts'}` };
      this.undoTimer = setTimeout(() => this.hideUndo(), UNDO_MS);
    },

    hideUndo() {
      if (this.undoTimer) { clearTimeout(this.undoTimer); this.undoTimer = null; }
      this.undo = null;
    },

    // Undo removes this employee's clear (or restores their earlier one). In office mode that brings the
    // card back for everyone — unless a teammate has cleared it too.
    async undoClear() {
      const undo = this.undo;
      const employeeID = this.myEmployeeID;
      if (!undo || !employeeID) return;
      this.hideUndo();
      const at = Date.now();
      this.patchLocalReadState(undo.entries, employeeID, (entry) => entry.prev);
      try {
        await this.writeReadState(undo.entries, employeeID, (entry) => (entry.prev === undefined ? deleteField() : entry.prev));
      } catch (err) {
        console.error('[AgentSidebar] Undo failed:', err);
        this.patchLocalReadState(undo.entries, employeeID, () => at);
        this.notify('error', "Couldn't undo — please try again.");
      }
    },

    // ── Phone sheet: swipe down to close ──────────────────────────────────────

    onSheetTouchStart(e) {
      if (!this.isViewportBelow(768) || !e.touches || e.touches.length !== 1) {
        this.sheetTouchStartY = null;
        return;
      }
      this.sheetTouchStartY = e.touches[0].clientY;
      this.sheetTouchDeltaY = 0;
    },

    onSheetTouchMove(e) {
      if (this.sheetTouchStartY === null || !e.touches || !e.touches.length) return;
      this.sheetTouchDeltaY = e.touches[0].clientY - this.sheetTouchStartY;
    },

    onSheetTouchEnd() {
      const pulled = this.sheetTouchDeltaY;
      this.sheetTouchStartY = null;
      this.sheetTouchDeltaY = 0;
      if (pulled > 80) this.close();
    },

    // ── Mock agent (no backend) ───────────────────────────────────────────────
    async runMockAgent() {
      this.isRunning = true;
      const delay = ms => new Promise(r => setTimeout(r, ms));
      const runRef = {};
      this.currentRun = runRef;

      await delay(700);
      if (this.currentRun !== runRef) return;

      const steps = [
        { label: 'Navigating to portal',   duration: 950 },
        { label: 'Locating login form',    duration: 450 },
        { label: 'Entering credentials',   duration: 750 },
        { label: 'Logging in',             duration: 1100 },
        { label: 'Searching for patient',  duration: 1300 },
        { label: 'Loading benefit details',duration: 900 },
        { label: 'Extracting eligibility', duration: 600 },
      ];

      for (const step of steps) {
        if (this.currentRun !== runRef) return;
        const id = `step-${step.label}`;
        this.messages.push({ id, type: 'step', content: step.label, status: 'running' });
        await delay(step.duration);
        if (this.currentRun !== runRef) return;
        const s = this.messages.find(m => m.id === id);
        if (s) this.$set(s, 'status', 'done');
        await delay(120);
      }

      await delay(400);
      if (this.currentRun !== runRef) return;

      const msgId = `mock-${Date.now()}`;
      this.messages.push({ id: msgId, type: 'assistant', content: '', isStreaming: true });
      const reply = `I've completed the eligibility verification. Here's a summary:\n\n- **Plan:** Delta Dental PPO\n- **Status:** Active\n- **Deductible:** $50 individual / $150 family\n- **Annual Max:** $1,500\n- **Remaining:** $1,200\n- **In-Network:** Yes\n\nThe patient is covered for the planned procedure.`;

      let i = 0;
      while (i < reply.length) {
        if (this.currentRun !== runRef) return;
        const chunkSize = Math.floor(Math.random() * 3) + 2;
        const msg = this.messages.find(m => m.id === msgId);
        if (msg) this.$set(msg, 'content', reply.slice(0, i + chunkSize));
        i += chunkSize;
        await delay(22);
      }

      const msg = this.messages.find(m => m.id === msgId);
      if (msg) { this.$set(msg, 'content', reply); this.$set(msg, 'isStreaming', false); }

      await delay(300);
      this.messages.push({
        id: `data-${Date.now()}`,
        type: 'data',
        title: 'Eligibility Results',
        data: { 'Plan': 'Delta Dental PPO', 'Status': 'Active', 'Deductible (Ind)': '$50', 'Annual Max': '$1,500', 'Remaining': '$1,200', 'In-Network': 'Yes' },
      });

      this.isRunning = false;
      this.maybeAppendSuggestions('automation');
    },

    async runMockOtp() {
      this.isRunning = true;
      const delay = ms => new Promise(r => setTimeout(r, ms));
      await delay(800);
      this.messages.push({ id: `otp-reply-${Date.now()}`, type: 'assistant', content: 'OTP accepted. Continuing verification...', isStreaming: false });
      this.isRunning = false;
    },
  },
};
</script>
